import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import type {
  IconProp,
  IconDefinition,
} from '@fortawesome/fontawesome-svg-core'
import { Icon, type IconProps } from '../../atoms/icon'
import { Image } from '../../atoms/image'
import { Text } from '../../atoms/text'
import { mtc, TypographyStyles } from '../../../utils/style-helpers'
import {
  listCustomBulletsStyles,
  listCustomBulletsItemStyles,
  numberIndicatorStyles,
  imageThumbnailStyles,
} from './styles'

type ListCustomBulletsVariant = 'icon' | 'number' | 'image'

interface ListCustomBulletsContextValue {
  variant: ListCustomBulletsVariant
  getNextIndex: () => number
}

const ListCustomBulletsContext = createContext<ListCustomBulletsContextValue>({
  variant: 'icon',
  getNextIndex: () => 0,
})

// --- ListCustomBullets.Item ---

export interface ListCustomBulletsItemProps extends HTMLAttributes<HTMLLIElement> {
  /** Heading text displayed as label-md */
  heading?: string
  /** Description text displayed as body-sm neutral-600 */
  description?: string
  /** Icon for the icon variant */
  icon?: IconProp | IconDefinition
  /** Icon color — Tailwind text-* class. Defaults to 'text-neutral-0' (white on dark bg) */
  iconColor?: string
  /** Icon background color — uses Icon atom's backgroundColor prop. Defaults to 'black' (neutral-900) */
  iconBackgroundColor?: IconProps['backgroundColor']
  /** Icon size — uses Icon atom's size prop. Defaults to '2xs' (24px). */
  iconSize?: IconProps['size']
  /** Number indicator background — Tailwind bg-* class. Defaults to 'bg-neutral-900' */
  numberBgColor?: string
  /** Number indicator text — Tailwind text-* class. Defaults to 'text-neutral-0' */
  numberTextColor?: string
  /** Image source and alt for the image variant */
  image?: { src: string; alt: string }
}

function ListCustomBulletsItem({
  heading,
  description,
  icon,
  iconColor = 'text-neutral-0',
  iconBackgroundColor = 'black',
  iconSize = '2xs',
  numberBgColor = 'bg-neutral-900',
  numberTextColor = 'text-neutral-0',
  image,
  className,
  children,
  ...rest
}: ListCustomBulletsItemProps) {
  const { variant, getNextIndex } = useContext(ListCustomBulletsContext)
  const indexRef = useRef<number | null>(null)

  if (variant === 'number' && indexRef.current === null) {
    indexRef.current = getNextIndex()
  }

  const renderLeading = () => {
    switch (variant) {
      case 'icon':
        return icon ? (
          <Icon
            icon={icon}
            color={iconColor}
            backgroundColor={iconBackgroundColor}
            backgroundStyle="rounded"
            size={iconSize}
          />
        ) : null
      case 'number':
        return (
          <span
            className={mtc(
              numberIndicatorStyles(),
              numberBgColor,
              numberTextColor
            )}
          >
            {indexRef.current}
          </span>
        )
      case 'image':
        return image ? (
          <div className={imageThumbnailStyles()}>
            <Image
              src={image.src}
              alt={image.alt}
              objectFit="cover"
              className="w-full h-full"
            />
          </div>
        ) : null
      default:
        return variant satisfies never
    }
  }

  const renderContent = () => {
    if (children) return <div className="flex-1">{children}</div>

    return (
      <div className="flex-1">
        {heading && <Text variant={TypographyStyles.LABEL_MD}>{heading}</Text>}
        {description && (
          <Text variant={TypographyStyles.BODY_SM} className="text-neutral-600">
            {description}
          </Text>
        )}
      </div>
    )
  }

  // For icon/number: align leading element with heading (if visible) or center with description
  // For image: always center with the whole heading+description block (handled via items-center in CVA)
  const alignmentClass =
    (variant === 'icon' || variant === 'number') && !heading
      ? 'items-center'
      : ''

  return (
    <li
      className={mtc(
        listCustomBulletsItemStyles({ variant }),
        alignmentClass,
        className
      )}
      {...rest}
    >
      {renderLeading()}
      {renderContent()}
    </li>
  )
}

ListCustomBulletsItem.displayName = 'ListCustomBulletsItem'

// --- ListCustomBullets (root) ---

export interface ListCustomBulletsProps extends HTMLAttributes<
  HTMLUListElement | HTMLOListElement
> {
  /** List display variant */
  variant: ListCustomBulletsVariant
  children: ReactNode
  className?: string
}

function ListCustomBulletsRoot({
  variant,
  children,
  className,
  ...rest
}: ListCustomBulletsProps) {
  const counterRef = useRef(0)
  counterRef.current = 0

  const contextValue = useMemo(() => {
    const getNextIndex = () => {
      counterRef.current += 1
      return counterRef.current
    }
    return { variant, getNextIndex }
  }, [variant])

  const Tag = variant === 'number' ? 'ol' : 'ul'

  return (
    <ListCustomBulletsContext.Provider value={contextValue}>
      <Tag
        className={mtc(listCustomBulletsStyles({ variant }), className)}
        {...rest}
      >
        {children}
      </Tag>
    </ListCustomBulletsContext.Provider>
  )
}

ListCustomBulletsRoot.displayName = 'ListCustomBullets'

export const ListCustomBullets = Object.assign(ListCustomBulletsRoot, {
  Item: ListCustomBulletsItem,
})
